import { useChat, type UseChatHelpers } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';

import { getApiToken } from '@/utils/auth';

import {
  conversationHistoryAtom,
  extendedReasoningAtom,
  modelKeyAtom,
  patchConversationMessages,
  renameConversation,
} from '../state';
import { PathwayUIMessage } from '../types';

import { store } from '@/atoms';
import { sessionOverridesAtom } from '@/atoms/sessionOverrides';
import { env } from '@/env';

/**
 * Body shape the server accepts. The backend's generated `ChatRequest` type
 * is shaped around the OpenAPI schema, which can't represent the AI SDK's
 * `UIMessage.parts` union as-is; the wire format is identical but the types
 * don't line up nominally, so we declare the shape we actually send here.
 */
interface ChatRequestBody {
  id: string;
  messages: PathwayUIMessage[];
  modelKey?: string;
  thinking: boolean;
}

export type SmartSearchChat = UseChatHelpers<PathwayUIMessage>;

interface UseSmartSearchChatOptions {
  /** Conversation id — drives persistence and the server-side chat key. */
  id: string;
  /** Prior messages to hydrate the chat with. */
  initialMessages: PathwayUIMessage[];
}

/**
 * Wraps `useChat` with the Pathway XP chat transport:
 *  - Auth + session-override headers (the generated XP client's interceptors
 *    don't apply because `useChat` uses its own fetch).
 *  - Shapes the POST body as `ChatRequest` (adds `modelKey` when selected).
 *  - Persists the live message list back to `conversationHistoryAtom` on
 *    every finish.
 */
export const useSmartSearchChat = ({
  id,
  initialMessages,
}: UseSmartSearchChatOptions): SmartSearchChat =>
  useChat<PathwayUIMessage>({
    id,
    messages: initialMessages,
    transport: new DefaultChatTransport<PathwayUIMessage>({
      api: `${env.REACT_APP_XP_API_URL}/chat`,
      credentials: 'include',
      headers: async () => {
        const token = await getApiToken();
        const headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
        };
        if (store.get(sessionOverridesAtom).has('updateResource')) {
          headers['X-Pathway-Feature-HideAllXylemeReferences'] = 'true';
        }
        return headers;
      },
      prepareSendMessagesRequest: ({ id: chatId, messages }) => {
        const modelKey = store.get(modelKeyAtom);
        const body: ChatRequestBody = {
          id: chatId,
          messages,
          thinking: store.get(extendedReasoningAtom),
          ...(modelKey ? { modelKey } : {}),
        };
        return { body };
      },
    }),
    onData: part => {
      switch (part.type) {
        case 'data-title':
          renameConversation(id, part.data.title);
          break;
      }
    },
    onFinish: ({ messages }) => {
      const exists = store.get(conversationHistoryAtom).some(c => c.id === id);
      if (exists) {
        patchConversationMessages(id, messages);
      }
    },
  });
